<template>
  <div>
    <breadcrumb :items="breadcrumbs" />
    <v-container fluid>
      <div class="d-flex mb-8 align-center">
        <h2 class="font-weight-bold">{{ $t('carbonCredit.manageTrees.bulkTreeDataImport') }}</h2>
        <v-spacer></v-spacer>
        <v-btn depressed color="primary" class="mr-5" outlined @click="$router.back()">
          {{ $t('back') }}
        </v-btn>
      </div>

      <v-card elevation="0" class="px-5">
        <v-card-text class="text-body-1 black--text">
          <div v-if="importing" class="d-flex align-center justify-center white flex-column px-4 py-5">
            <v-progress-circular :size="75" color="primary" indeterminate></v-progress-circular>
            <p class="mt-10 text-center">
              {{ $t('farm.importingNote') }}
            </p>
          </div>
          <div v-show="!importing">
            <p>{{ $t('carbonCredit.manageTrees.downloadTemplateToBulkImportMessage') }}</p>

            <label for="download-option" class="mb-1 d-block">
              {{ $t('carbonCredit.manageTrees.selectTemplate') }}
            </label>
            <v-select
              style="max-width: 360px"
              id="download-option"
              outlined
              :items="downloadOptions"
              v-model="selectedDownloadOption"
              hide-details
            ></v-select>

            <v-btn
              download
              href="/files/my-tree/bulk-import-tree.csv"
              color="primary"
              class="my-3"
              large
              v-if="selectedDownloadOptionValue == 'CSV'"
            >
              {{ $t('oma.downloadCSVFormat') }}
              <v-icon small class="ml-2">mdi-tray-arrow-down</v-icon>
            </v-btn>

            <div class="dropzone">
              <label
                v-if="!file"
                class="d-flex flex-column align-center justify-center px-3 py-3"
                :class="{ active: dragging }"
                @dragenter="dragging = true"
                @dragleave="dragging = false"
                @drop.prevent="handleFileChangeEvent"
              >
                <img width="50" v-if="selectedDownloadOptionValue === 'CSV'" src="/img/csv-upload.svg" />
                <input
                  class="d-none"
                  type="file"
                  @change="handleFileChangeEvent"
                  :accept="selectedDownloadOption.accept"
                />
                <span class="font-weight-medium pt-2 text-decoration-underline blue--text darken-4">
                  {{ $t('farm.selectFileToUpload') }}
                </span>
                <span>
                  {{ $t('farm.dropHere') }}
                </span>
              </label>
              <div
                v-else
                class="d-flex flex-column justify-center align-center"
                style="position: relative; width: 100%; height: 100%"
              >
                <img width="50" v-if="selectedDownloadOptionValue === 'CSV'" src="/img/csv-upload.svg" />
                <div class="mt-1">
                  <span class="text-body-1">
                    {{ file.name }}
                  </span>
                </div>
                <v-alert dense type="error" class="mt-4" style="min-width: 70%" outlined v-show="fileError">
                  {{ fileError }}
                </v-alert>
                <v-btn @click="removeFile" icon text class="ma-2" style="position: absolute; top: 0; right: 0">
                  <v-icon dark>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
            <v-divider class="my-5 px-0"></v-divider>
            <p class="pt-3 text-body-2">
              <span class="red--text">{{ $t('oma.importantNote') }}: </span>: {{ $t('farm.importantNoteDetail') }}
            </p>
            <v-card-actions class="px-0">
              <v-spacer></v-spacer>
              <v-btn
                :disabled="!canImportFile"
                @click="handleBulkImport"
                width="112"
                color="primary"
                class="btn-11 btn-22"
              >
                {{ $t('oma.import') }}
              </v-btn>
            </v-card-actions>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      breadcrumbs: [
        { text: 'Dashboard', disabled: false, href: '/dashboard', exact: true },
        { text: 'Carbon Credit', disabled: false, href: '/carbon-credit/projects' },
        { text: 'Manage Trees', disabled: false, href: '/carbon-credit/manage-trees' },
        { text: 'Bulk Tree Data Import', disabled: true },
      ],
      downloadOptions: [{ text: 'Import CSV Format', value: 'CSV', accept: '.csv,.xls,.xlsx' }],
      selectedDownloadOptionValue: 'CSV',
      dragging: false,
      file: null,
      fileError: null,
      importing: false,
    };
  },

  computed: {
    selectedDownloadOption() {
      return this.downloadOptions.find((option) => option.value === this.selectedDownloadOptionValue);
    },
    canImportFile() {
      return this.file && !this.fileError;
    },
  },

  methods: {
    handleFileChangeEvent(event) {
      this.dragging = false;
      let files;
      if (event instanceof DragEvent) {
        files = event.dataTransfer.files;
      } else {
        files = event.target?.files;
      }
      if (files instanceof FileList) {
        let file;
        for (let i = 0; i < files.length; i++) {
          const currentFileExtension = files.item(i).name.split('.').pop();
          if (this.selectedDownloadOption.accept.split(',').includes(`.${currentFileExtension}`)) {
            file = files.item(i);
            break;
          }
        }
        if (file) {
          this.file = file;
        } else {
          this.file = files.item(0);
          this.fileError = this.$t('activationKey.incorectFileType');
        }
      }
    },
    removeFile() {
      this.file = null;
      this.fileError = '';
    },
    async handleBulkImport() {
      try {
        this.importing = true;
        const formData = new FormData();
        formData.append('file', this.file);
        const response = await axios.post('/manage-trees/bulk-import', formData);
        const data = response.data;
        if (data.success) {
          const result = data.data;
          console.log(result);
          this.$router.push({
            name: 'bulkImportTreesDetail',
            params: { id: result.id },
            query: {
              imported: 1,
            },
          });
        } else {
          this.$notify({
            type: 'error',
            title: data.message || this.$t('serverError'),
          });
        }
      } catch (error) {
        console.error(error);
        this.$notify({
          type: 'error',
          title: this.$t('serverError'),
        });
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.dropzone {
  height: 280px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed $gray;
  border-radius: 8px;
}
.dropzone label {
  cursor: pointer;
  width: 100%;
  height: 100%;
}
.dropzone label:hover,
.dropzone label.active {
  background-color: #f5f5f5;
}
</style>
